import React from 'react';
import { motion } from 'framer-motion';
import { Search, Link as LinkIcon, Code, Brain, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useContent, useLanguage, useUI, useLocalPath } from '../context/LanguageContext';

const iconMap = { Search, Link: LinkIcon, Code, Brain };

const WhatWeDoSection = () => {
  const content = useContent();
  const localPath = useLocalPath();
  const ui = useUI();

  return (
    <section id="what-we-do" className="relative py-32 border-t border-[#21262d]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-10 mb-20">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-mono text-[11px] tracking-[0.2em] text-[#3b82f6] uppercase block mb-4">
                {ui.whatWeDoLabel}
              </span>
              <h2 className="text-[clamp(36px,4.5vw,64px)] font-black leading-[0.95] tracking-[-0.02em]">
                <span className="text-[#e6edf3]">{ui.buildOperateScale[0]} </span>
                <span className="text-[#7d8590]">{ui.buildOperateScale[1]} </span>
                <span className="text-[#3b82f6]">{ui.buildOperateScale[2]}</span>
              </h2>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:pt-4 flex items-end"
          >
            <p className="font-mono text-[12px] leading-[1.8] text-[#7d8590] max-w-md">
              {ui.whatWeDoDescription}
            </p>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {content.whatWeDo.map((item, i) => {
            const IconComponent = iconMap[item.icon];
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-[#161b22] border border-[#21262d] rounded-lg p-8 hover:border-[#3b82f6]/30 transition-all duration-500"
              >
                <div className="w-10 h-10 rounded-lg border border-[#21262d] flex items-center justify-center bg-[#0d1117] group-hover:border-[#3b82f6]/30 transition-colors duration-300 mb-5">
                  <IconComponent size={18} className="text-[#3b82f6]" />
                </div>
                <h3 className="text-lg font-bold text-[#e6edf3] mb-3 group-hover:text-[#3b82f6] transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="font-mono text-[11px] leading-[1.7] text-[#7d8590]">
                  {item.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-12 text-center"
        >
          <Link
            to={localPath('/services/')}
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.1em] uppercase text-[#3b82f6] hover:text-[#60a5fa] transition-colors group/link"
          >
            {ui.exploreAllServices}
            <ArrowRight size={13} className="group-hover/link:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default WhatWeDoSection;
